import { useState } from "react";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { DateField } from "@/components/common/masked-form-field";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  portalAccounts,
  portalInsurance,
  portalTransfers,
  type PortalCollectionApi,
} from "@/features/portal/api";
import { centsToDollars, dollarsToCents } from "@/features/worksheets/money";
import { ACCOUNT_CLASS_OPTIONS } from "@/features/worksheets/types";
import { isoToMaskedDate, maskedDateToISO } from "@/lib/masks";
import {
  CollectionCard,
  type Column,
  type PortalRecord,
  usePortalCollection,
} from "./collection-card";
import { PortalCrudDialog } from "./crud-dialog";
import { CheckboxInput, FIELD_LABEL, MoneyInput, SelectInput } from "./fields";
import { bool, dash, money, num, str } from "./record";
import type { WorksheetHeader } from "./portal-financials";

const nonNegMoney = Yup.string().test(
  "non-negative",
  "Must be zero or more",
  (v) => !v || dollarsToCents(v) >= 0,
);
const centsInit = (r: PortalRecord | null, key: string) =>
  centsToDollars(num(r ?? {}, key) ?? 0);

const CLASS_LABEL: Record<string, string> = Object.fromEntries(
  ACCOUNT_CLASS_OPTIONS.map((o) => [o.value, o.label]),
);

// --- cash on hand (header) ---------------------------------------------------

function CashOnHand({
  header,
  save,
}: {
  header: WorksheetHeader;
  save: (patch: Record<string, unknown>) => void;
}) {
  const [local, setLocal] = useState(centsToDollars(num(header as PortalRecord, "cash_on_hand") ?? 0));
  return (
    <Card className="grid gap-2 p-4 sm:max-w-xs">
      <Label className={FIELD_LABEL}>Cash on hand</Label>
      <div className="relative">
        <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm">
          $
        </span>
        <Input
          className="pl-7"
          value={local}
          inputMode="decimal"
          placeholder="0.00"
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) setLocal(raw);
          }}
          onBlur={() => save({ cash_on_hand: dollarsToCents(local) })}
        />
      </div>
    </Card>
  );
}

// --- Accounts ----------------------------------------------------------------

interface AccountValues {
  account_class: string;
  institution: string;
  account_type: string;
  account_number: string;
  account_value: string;
  loan_balance: string;
  credit_limit: string;
  qs_rate: string;
  key_access: boolean;
}

function AccountDialog({
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  record: PortalRecord | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const editing = !!record;
  const r = record ?? {};
  const initial: AccountValues = {
    account_class: str(r, "account_class") || "P",
    institution: str(r, "institution"),
    account_type: str(r, "account_type"),
    account_number: "", // write-only; masked label shown separately
    account_value: centsInit(record, "account_value"),
    loan_balance: centsInit(record, "loan_balance"),
    credit_limit: centsInit(record, "credit_limit"),
    qs_rate: str(r, "qs_rate"),
    key_access: bool(r, "key_access"),
  };

  async function onSave(v: AccountValues) {
    const payload: Record<string, unknown> = {
      account_class: v.account_class,
      institution: v.institution,
      account_type: v.account_type,
      account_value: dollarsToCents(v.account_value),
      loan_balance: dollarsToCents(v.loan_balance),
      credit_limit: dollarsToCents(v.credit_limit),
      qs_rate: v.qs_rate,
      key_access: v.key_access,
    };
    // Only send the account number when the user actually typed one.
    if (v.account_number.trim()) payload.account_number = v.account_number.trim();
    if (editing && record) await portalAccounts.update(record.id, payload);
    else await portalAccounts.create(payload);
  }

  const maskedLabel = str(r, "account_number_label");

  return (
    <PortalCrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={editing}
      noun="Account"
      className="sm:max-w-2xl"
      initialValues={initial}
      validationSchema={Yup.object({
        institution: Yup.string().trim().required("Required"),
        account_value: nonNegMoney,
        loan_balance: nonNegMoney,
        credit_limit: nonNegMoney,
      })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <SelectInput name="account_class" label="Account type" options={ACCOUNT_CLASS_OPTIONS} />
      <div className="grid grid-cols-2 gap-4">
        <FormField name="institution" label="Institution" />
        <FormField name="account_type" label="Account label" />
      </div>
      <FormField
        name="account_number"
        label={maskedLabel ? `Account number (currently ${maskedLabel})` : "Account number"}
        placeholder={editing ? "Leave blank to keep current" : ""}
      />
      <div className="grid grid-cols-3 gap-4">
        <MoneyInput name="account_value" label="Balance / value" />
        <MoneyInput name="loan_balance" label="Loan balance" />
        <MoneyInput name="credit_limit" label="Credit limit" />
      </div>
      <FormField name="qs_rate" label="Interest rate %" inputMode="decimal" />
      <CheckboxInput name="key_access" label="Has access to a key / login" />
    </PortalCrudDialog>
  );
}

function AccountsSection() {
  const { rows, loading, reload } = usePortalCollection(
    portalAccounts as PortalCollectionApi<PortalRecord>,
  );
  const [dialog, setDialog] = useState<{ open: boolean; record: PortalRecord | null }>({
    open: false,
    record: null,
  });
  const columns: Column<PortalRecord>[] = [
    { header: "Type", cell: (r) => CLASS_LABEL[str(r, "account_class")] ?? dash(str(r, "account_class")) },
    { header: "Institution", cell: (r) => dash(str(r, "institution")) },
    { header: "Number", cell: (r) => dash(str(r, "account_number_label")) },
    { header: "Value", cell: (r) => money(r, "account_value") },
  ];
  return (
    <>
      <CollectionCard
        title="Accounts & investments"
        description="Checking, savings, investment, virtual currency, retirement, and available credit."
        addLabel="Add Account"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(record) => setDialog({ open: true, record })}
        onDelete={(row) => portalAccounts.remove(row.id)}
        onDeleted={reload}
      />
      <AccountDialog
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}

// --- Insurance ---------------------------------------------------------------

interface InsuranceValues {
  company: string;
  policy_no: string;
  cash_value: string;
  loan_balance: string;
}

function InsuranceDialog({
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  record: PortalRecord | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const editing = !!record;
  const r = record ?? {};
  const initial: InsuranceValues = {
    company: str(r, "company"),
    policy_no: str(r, "policy_no"),
    cash_value: centsInit(record, "cash_value"),
    loan_balance: centsInit(record, "loan_balance"),
  };

  async function onSave(v: InsuranceValues) {
    const payload = {
      company: v.company,
      policy_no: v.policy_no,
      cash_value: dollarsToCents(v.cash_value),
      loan_balance: dollarsToCents(v.loan_balance),
    };
    if (editing && record) await portalInsurance.update(record.id, payload);
    else await portalInsurance.create(payload);
  }

  return (
    <PortalCrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={editing}
      noun="Policy"
      initialValues={initial}
      validationSchema={Yup.object({
        company: Yup.string().trim().required("Required"),
        cash_value: nonNegMoney,
        loan_balance: nonNegMoney,
      })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField name="company" label="Insurance company" />
        <FormField name="policy_no" label="Policy no." />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <MoneyInput name="cash_value" label="Cash value" />
        <MoneyInput name="loan_balance" label="Loan balance" />
      </div>
    </PortalCrudDialog>
  );
}

function InsuranceSection() {
  const { rows, loading, reload } = usePortalCollection(
    portalInsurance as PortalCollectionApi<PortalRecord>,
  );
  const [dialog, setDialog] = useState<{ open: boolean; record: PortalRecord | null }>({
    open: false,
    record: null,
  });
  const columns: Column<PortalRecord>[] = [
    { header: "Company", cell: (r) => dash(str(r, "company")) },
    { header: "Policy no.", cell: (r) => dash(str(r, "policy_no")) },
    { header: "Cash value", cell: (r) => money(r, "cash_value") },
  ];
  return (
    <>
      <CollectionCard
        title="Life insurance"
        addLabel="Add Policy"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(record) => setDialog({ open: true, record })}
        onDelete={(row) => portalInsurance.remove(row.id)}
        onDeleted={reload}
      />
      <InsuranceDialog
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}

// --- Transfers ---------------------------------------------------------------

interface TransferValues {
  transfer_type: string;
  description: string;
  amount: string;
  transfer_date: string;
  to_whom: string;
}

const TRANSFER_TYPE_OPTIONS = [
  { value: "N", label: "Not at arm's length" },
  { value: "R", label: "Related party" },
];

function TransferDialog({
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  record: PortalRecord | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const editing = !!record;
  const r = record ?? {};
  const initial: TransferValues = {
    transfer_type: str(r, "transfer_type") || "N",
    description: str(r, "description"),
    amount: centsInit(record, "amount"),
    transfer_date: isoToMaskedDate(str(r, "transfer_date")),
    to_whom: str(r, "to_whom"),
  };

  async function onSave(v: TransferValues) {
    const payload = {
      transfer_type: v.transfer_type,
      description: v.description,
      amount: dollarsToCents(v.amount),
      transfer_date: v.transfer_date ? maskedDateToISO(v.transfer_date) || null : null,
      to_whom: v.to_whom,
    };
    if (editing && record) await portalTransfers.update(record.id, payload);
    else await portalTransfers.create(payload);
  }

  return (
    <PortalCrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={editing}
      noun="Transfer"
      initialValues={initial}
      validationSchema={Yup.object({
        description: Yup.string().trim().required("Required"),
        amount: nonNegMoney,
      })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <SelectInput name="transfer_type" label="Transfer type" options={TRANSFER_TYPE_OPTIONS} />
      <FormField name="description" label="Description" />
      <div className="grid grid-cols-2 gap-4">
        <MoneyInput name="amount" label="Amount" />
        <DateField name="transfer_date" label="Date" />
      </div>
      <FormField name="to_whom" label="Transferred to" />
    </PortalCrudDialog>
  );
}

function TransfersSection() {
  const { rows, loading, reload } = usePortalCollection(
    portalTransfers as PortalCollectionApi<PortalRecord>,
  );
  const [dialog, setDialog] = useState<{ open: boolean; record: PortalRecord | null }>({
    open: false,
    record: null,
  });
  const columns: Column<PortalRecord>[] = [
    { header: "Description", cell: (r) => dash(str(r, "description")) },
    { header: "Amount", cell: (r) => money(r, "amount") },
    { header: "Date", cell: (r) => dash(str(r, "transfer_date")) },
    { header: "To", cell: (r) => dash(str(r, "to_whom")) },
  ];
  return (
    <>
      <CollectionCard
        title="Asset transfers"
        description="Assets transferred for less than full value in the last 10 years."
        addLabel="Add Transfer"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(record) => setDialog({ open: true, record })}
        onDelete={(row) => portalTransfers.remove(row.id)}
        onDeleted={reload}
      />
      <TransferDialog
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}

interface Props {
  header: WorksheetHeader;
  save: (patch: Record<string, unknown>) => void;
}

export function BankingTab({ header, save }: Props) {
  return (
    <div className="grid gap-6">
      <CashOnHand header={header} save={save} />
      <AccountsSection />
      <InsuranceSection />
      <TransfersSection />
    </div>
  );
}
